import { useState, useEffect, useRef } from "react";
import { api, API } from "@/lib/api";
import { Download, Mail, Search, Upload, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function EmailsPanel() {
  const [list, setList] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/marketing/emails")
      .then(({ data }) => setList(data))
      .catch(() => toast.error("Impossible de charger les emails"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = list.filter(x => {
    const q = search.toLowerCase();
    return !q || x.email.toLowerCase().includes(q) || x.firstname?.toLowerCase().includes(q) || x.lastname?.toLowerCase().includes(q);
  });

  const sourceLabel = (sources = []) => {
    const parts = [];
    if (sources.includes("order")) parts.push("Commande");
    if (sources.includes("reservation")) parts.push("Réservation");
    if (sources.includes("manual")) parts.push("Import CSV");
    return parts.join(" + ") || "—";
  };

  const exportCsv = () => {
    const headers = ["Email", "Prénom", "Nom", "Téléphone", "Source", "Commandes", "Réservations", "Première", "Dernière activité"];
    const rows = filtered.map(x => [
      x.email, x.firstname || "", x.lastname || "", x.phone || "",
      sourceLabel(x.sources), x.orders_count || 0, x.reservations_count || 0,
      (x.first_seen || "").slice(0, 10), (x.last_activity || "").slice(0, 10),
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${String(c).replaceAll('"','""')}"`).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `hanamiya-emails-${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const copyAll = () => {
    const emails = filtered.map(x => x.email).join(", ");
    navigator.clipboard.writeText(emails);
    toast.success(`${filtered.length} email${filtered.length > 1 ? "s" : ""} copié${filtered.length > 1 ? "s" : ""}`);
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("hana_admin_token");
      const res = await fetch(`${API}/admin/marketing/emails/import`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Erreur import");
      toast.success(`${data.imported} ajouté(s) · ${data.skipped_duplicates} doublons · ${data.skipped_invalid} invalides`);
      load();
    } catch (err) {
      toast.error(err.message || "Import échoué");
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const deleteManual = async (email) => {
    if (!window.confirm(`Supprimer ${email} de la base marketing ?`)) return;
    try {
      await api.delete(`/admin/marketing/emails/${encodeURIComponent(email)}`);
      toast.success("Contact supprimé");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Suppression impossible (contact non-manuel)");
    }
  };

  return (
    <div data-testid="emails-panel">
      <div className="flex flex-wrap items-center justify-between mb-6 gap-3">
        <div>
          <h1 className="font-display text-4xl text-foreground flex items-center gap-3"><Mail size={28} /> Emails clients</h1>
          <p className="text-muted-foreground text-sm mt-1">{list.length} abonné{list.length > 1 ? "s" : ""} (opt-in commandes, réservations et imports manuels)</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <label
            data-testid="import-emails-label"
            className={`cursor-pointer inline-flex items-center gap-2 px-4 py-2 border-2 border-dashed border-primary text-primary rounded-sm hover:bg-primary hover:text-white transition text-sm font-medium ${importing ? "opacity-60 pointer-events-none" : ""}`}
          >
            <Upload size={14} /> {importing ? "Import en cours…" : "Importer CSV"}
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              data-testid="import-emails-input"
              onChange={handleImport}
            />
          </label>
          <button onClick={copyAll} data-testid="copy-emails-btn" className="px-4 py-2 border border-border rounded-sm text-sm hover:bg-secondary">Copier</button>
          <button onClick={exportCsv} data-testid="export-csv-btn" className="bg-primary text-white px-4 py-2 rounded-sm flex items-center gap-2 hover:bg-primary/90 text-sm"><Download size={14} /> Exporter CSV</button>
        </div>
      </div>

      <div className="mb-6 p-3 bg-secondary/40 border border-border rounded-sm text-xs text-muted-foreground">
        <strong className="text-foreground">Format CSV attendu :</strong> une ligne d&apos;en-tête avec les colonnes <code>prenom</code>, <code>nom</code>, <code>email</code> (séparateur virgule ou point-virgule, UTF-8). Les doublons sont ignorés automatiquement.
      </div>

      <div className="relative max-w-md mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
        <Input data-testid="emails-search" placeholder="Rechercher…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 h-11 rounded-sm" />
      </div>

      {loading ? (
        <div className="text-muted-foreground">Chargement…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground border border-dashed border-border rounded-sm">
          {list.length === 0 ? "Aucun contact pour l'instant. Importez un CSV pour commencer." : "Aucun résultat."}
        </div>
      ) : (
        <div className="bg-white border border-border rounded-sm overflow-hidden overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-secondary text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left p-3">Email</th>
                <th className="text-left p-3">Client</th>
                <th className="text-left p-3">Téléphone</th>
                <th className="text-left p-3">Source</th>
                <th className="text-center p-3">Cmd</th>
                <th className="text-center p-3">Rés.</th>
                <th className="text-left p-3">Dernière</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((x, i) => {
                const isManualOnly = (x.sources || []).length === 1 && x.sources[0] === "manual";
                return (
                  <tr key={x.email} className={i % 2 ? "bg-secondary/30" : ""}>
                    <td className="p-3 font-mono text-primary">{x.email}</td>
                    <td className="p-3">{[x.firstname, x.lastname].filter(Boolean).join(" ") || "—"}</td>
                    <td className="p-3 text-muted-foreground">{x.phone || "—"}</td>
                    <td className="p-3 text-xs text-muted-foreground">{sourceLabel(x.sources)}</td>
                    <td className="p-3 text-center">{x.orders_count || 0}</td>
                    <td className="p-3 text-center">{x.reservations_count || 0}</td>
                    <td className="p-3 text-muted-foreground">{(x.last_activity || "").slice(0, 10)}</td>
                    <td className="p-3">
                      {isManualOnly && (
                        <button
                          onClick={() => deleteManual(x.email)}
                          className="text-destructive hover:bg-destructive/10 p-1.5 rounded-sm"
                          aria-label="Supprimer"
                          data-testid={`del-email-${x.email}`}
                        ><Trash2 size={14} /></button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
